﻿(() => {
  const DAY_MAP = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
  const fmtCache = new Map();

  const onReady = (fn) => {
    if (document.readyState !== "loading") {
      fn();
    } else {
      document.addEventListener("DOMContentLoaded", fn, { once: true });
    }
  };

  const pad2 = (n) => String(Math.max(0, n)).padStart(2, "0");

  const parseDateKey = (value) => {
    const m = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return null;
    return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
  };

  const dateKey = (y, m, d) => `${y}-${pad2(m)}-${pad2(d)}`;

  const addDays = (key, days) => {
    const p = parseDateKey(key);
    if (!p) return key;
    const ref = new Date(Date.UTC(p.y, p.m - 1, p.d, 12, 0, 0, 0));
    ref.setUTCDate(ref.getUTCDate() + days);
    return dateKey(ref.getUTCFullYear(), ref.getUTCMonth() + 1, ref.getUTCDate());
  };

  const parseCutDays = (value) => {
    if (!value) return [];
    return String(value)
      .split(",")
      .map((item) => item.trim().toLowerCase())
      .map((item) => DAY_MAP[item])
      .filter((day) => day !== undefined);
  };

  const getFormatter = (locale, timeZone, options) => {
    const key = `${locale}|${timeZone}|${JSON.stringify(options)}`;
    if (!fmtCache.has(key)) {
      fmtCache.set(key, new Intl.DateTimeFormat(locale, { ...options, timeZone }));
    }
    return fmtCache.get(key);
  };

  const tzParts = (date, timeZone) => {
    const f = getFormatter("en-US", timeZone, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      weekday: "short",
      hourCycle: "h23"
    });
    const out = {};
    f.formatToParts(date).forEach((p) => {
      if (p.type !== "literal") out[p.type] = p.value;
    });

    const weekdayShort = String(out.weekday || "").toLowerCase();
    return {
      y: Number(out.year),
      m: Number(out.month),
      d: Number(out.day),
      h: Number(out.hour),
      min: Number(out.minute),
      s: Number(out.second),
      wd: DAY_MAP[weekdayShort] ?? date.getUTCDay()
    };
  };

  const tzOffsetMs = (date, timeZone) => {
    const p = tzParts(date, timeZone);
    const asUtc = Date.UTC(p.y, p.m - 1, p.d, p.h, p.min, p.s, 0);
    return asUtc - date.getTime();
  };

  const tzDateTimeToUtcMs = (dateK, hhmm, timeZone) => {
    const d = parseDateKey(dateK);
    const t = String(hhmm || "").match(/^(\d{1,2}):(\d{2})$/);
    if (!d || !t) return Number.NaN;
    const hh = Number(t[1]);
    const mm = Number(t[2]);
    const base = Date.UTC(d.y, d.m - 1, d.d, hh, mm, 0, 0);

    let guess = base;
    for (let i = 0; i < 4; i += 1) {
      const off = tzOffsetMs(new Date(guess), timeZone);
      const target = base - off;
      if (Math.abs(target - guess) < 1000) {
        guess = target;
        break;
      }
      guess = target;
    }
    return guess;
  };

  const weekdayIndex = (dateK, timeZone) => {
    const p = parseDateKey(dateK);
    if (!p) return 0;
    const ref = new Date(Date.UTC(p.y, p.m - 1, p.d, 12, 0, 0, 0));
    return tzParts(ref, timeZone).wd;
  };

  const nowDateKey = (timeZone) => {
    const p = tzParts(new Date(), timeZone);
    return dateKey(p.y, p.m, p.d);
  };

  const nextCutoffMs = (nowMs, timeLabel, cutDays, timeZone) => {
    const t = String(timeLabel || "18:00").match(/^(\d{1,2}):(\d{2})$/);
    const hhmm = t ? `${pad2(Number(t[1]))}:${pad2(Number(t[2]))}` : "18:00";
    let cursor = nowDateKey(timeZone);

    for (let i = 0; i < 10; i += 1) {
      const wd = weekdayIndex(cursor, timeZone);
      const candidateMs = tzDateTimeToUtcMs(cursor, hhmm, timeZone);
      if (!cutDays.includes(wd) && Number.isFinite(candidateMs) && candidateMs > nowMs) {
        return candidateMs;
      }
      cursor = addDays(cursor, 1);
    }

    return Number.NaN;
  };

  const formatDateTime = (iso, locale, timeZone) => {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "";

    const dateFmt = getFormatter(locale, timeZone, {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric"
    });
    const timeFmt = getFormatter(locale, timeZone, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    });
    const labelDate = dateFmt.format(date);
    const labelTime = timeFmt.format(date).replace(":", "h");
    const prettyDate = labelDate ? labelDate.charAt(0).toUpperCase() + labelDate.slice(1) : "";
    return `${prettyDate} \u00e0 ${labelTime}`;
  };

  const formatDateOnly = (key, locale, timeZone) => {
    const p = parseDateKey(key);
    if (!p) return "";
    const date = new Date(Date.UTC(p.y, p.m - 1, p.d, 12, 0, 0, 0));
    const dateFmt = getFormatter(locale, timeZone, {
      day: "2-digit",
      month: "long"
    });
    return dateFmt.format(date);
  };

  const addBusinessDays = (startKey, days, cutDays, timeZone) => {
    let cursor = startKey;
    let remaining = Math.max(0, Number(days) || 0);
    while (remaining > 0) {
      cursor = addDays(cursor, 1);
      const wd = weekdayIndex(cursor, timeZone);
      if (cutDays.includes(wd)) continue;
      remaining -= 1;
    }
    return cursor;
  };

  const buildDeliveryLabel = (row, locale, timeZone) => {
    if (!row) return "Livraison estim\u00e9e";
    const ds = Math.max(0, Number(row.dataset.ds || 0));
    const de = Math.max(ds, Number(row.dataset.de || ds));
    if (!ds && !de) return "Livraison estim\u00e9e";

    const cutDays = parseCutDays(row.dataset.cut || "sat,sun");
    const cutoff = nextCutoffMs(Date.now(), row.dataset.time || "18:00", cutDays, timeZone);
    const baseParts = tzParts(new Date(Number.isFinite(cutoff) ? cutoff : Date.now()), timeZone);
    const baseKey = dateKey(baseParts.y, baseParts.m, baseParts.d);
    const startKey = addBusinessDays(baseKey, ds, cutDays, timeZone);
    const endKey = addBusinessDays(baseKey, de, cutDays, timeZone);
    const startLabel = formatDateOnly(startKey, locale, timeZone);
    const endLabel = formatDateOnly(endKey, locale, timeZone);
    if (!startLabel || !endLabel) return "Livraison estim\u00e9e";
    const nature = String(row.dataset.lfdlDelivery || '').startsWith('service_') ? 'Réalisation' : 'Livraison';
    return `${nature} estimée entre le ${startLabel} et le ${endLabel}`;
  };

  const parseSlots = (raw) => {
    if (!raw) return [];
    return String(raw)
      .split("|")
      .map((slot) => slot.trim())
      .filter(Boolean);
  };

  const ensureUrgencyRoot = (frame, head) => {
    let root = frame.querySelector(".lfdl-cart__urgency-wrap");
    if (!root) {
      root = document.createElement("div");
      root.className = "lfdl-cart__urgency-wrap";
      root.hidden = true;
      root.innerHTML = `
        <div class="lfdl-cart__urgency lfdl-cart__urgency--shipping" hidden></div>
        <div class="lfdl-cart__urgency lfdl-cart__urgency--booking" hidden></div>
      `;
      frame.insertBefore(root, head || frame.firstChild);
    }
    return {
      root,
      shipping: root.querySelector(".lfdl-cart__urgency--shipping"),
      booking: root.querySelector(".lfdl-cart__urgency--booking")
    };
  };

  const countdownMMSS = (ms) => {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const mm = Math.floor(totalSeconds / 60);
    const ss = totalSeconds % 60;
    return `${pad2(mm)}:${pad2(ss)}`;
  };

  const countdownHHMM = (ms) => {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const hh = Math.floor(totalSeconds / 3600);
    const mm = Math.floor((totalSeconds % 3600) / 60);
    return `${pad2(hh)}:${pad2(mm)}`;
  };

  const buildGroupHeader = (label) => {
    const header = document.createElement("div");
    header.className = "lfdl-cart__group";
    const title = document.createElement("div");
    title.className = "lfdl-cart__group-label";
    title.textContent = label;
    header.appendChild(title);
    return header;
  };

  const decorateBookingRows = (bookingRows, locale, timeZone) => {
    bookingRows.forEach((row) => {
      row.classList.add("lfdl-cart__row--booking");
      const line = row.querySelector("[data-booking-line]");
      const slots = parseSlots(row.dataset.bookingSlots);
      const fallback = slots.length ? formatDateTime(slots[0], locale, timeZone) : "";
      const configured = String(row.dataset.bookingLabel || "").trim();
      const display = configured || fallback;

      if (line && display) {
        line.textContent = `Rendez-vous : ${display}`;
      } else if (!line && display) {
        const cMeta = row.querySelector(".c-meta");
        if (cMeta) {
          const span = document.createElement("span");
          span.className = "muted lfdl-cart__booking-line";
          span.setAttribute("data-booking-line", "1");
          span.textContent = `Rendez-vous : ${display}`;
          cMeta.appendChild(span);
        }
      }
    });
  };

  /* LFDL PANIER GROUPES ASTROLOGIE V1 */
  const splitRows = (rows) => {
    const lfdl5Rows = [];
    const lfdl10Rows = [];
    const astrologyRows = [];
    const constellationRows = [];
    const bookingRows = [];
    const otherRows = [];

    rows.forEach((row) => {
      const deliveryType = String(row.dataset.lfdlDelivery || "").trim();
      const byFlag = String(row.dataset.lfdlBooking || "") === "1";
      const bySlots = parseSlots(row.dataset.bookingSlots).length > 0;
      if (byFlag || bySlots) {
        bookingRows.push(row);
      } else if (deliveryType === "product_5") {
        lfdl5Rows.push(row);
      } else if (deliveryType === "product_10") {
        lfdl10Rows.push(row);
      } else if (deliveryType === "service_astrologie") {
        astrologyRows.push(row);
      } else if (deliveryType === "service_constellation") {
        constellationRows.push(row);
      } else {
        otherRows.push(row);
      }
    });

    return { lfdl5Rows, lfdl10Rows, astrologyRows, constellationRows, bookingRows, otherRows };
  };

  const renderGroups = (frame, lfdl5Rows, lfdl10Rows, astrologyRows, constellationRows, bookingRows, otherRows, locale, timeZone) => {
    frame.querySelectorAll(".lfdl-cart__group").forEach((node) => node.remove());
    [...lfdl5Rows, ...lfdl10Rows, ...astrologyRows, ...constellationRows, ...bookingRows, ...otherRows].forEach((row) => row.remove());

    if (lfdl5Rows.length) {
      frame.appendChild(buildGroupHeader(buildDeliveryLabel(lfdl5Rows[0], locale, timeZone)));
      lfdl5Rows.forEach((row) => frame.appendChild(row));
    }
    if (lfdl10Rows.length) {
      frame.appendChild(buildGroupHeader(buildDeliveryLabel(lfdl10Rows[0], locale, timeZone)));
      lfdl10Rows.forEach((row) => frame.appendChild(row));
    }
    if (astrologyRows.length) {
      frame.appendChild(buildGroupHeader(buildDeliveryLabel(astrologyRows[0], locale, timeZone)));
      astrologyRows.forEach((row) => frame.appendChild(row));
    }
    if (constellationRows.length) {
      frame.appendChild(buildGroupHeader(buildDeliveryLabel(constellationRows[0], locale, timeZone)));
      constellationRows.forEach((row) => frame.appendChild(row));
    }
    if (bookingRows.length) {
      frame.appendChild(buildGroupHeader("Rendez-vous"));
      bookingRows.forEach((row) => frame.appendChild(row));
    }
    if (otherRows.length) {
      const hasPreviousGroups = lfdl5Rows.length || lfdl10Rows.length || astrologyRows.length || constellationRows.length || bookingRows.length;
      const label = hasPreviousGroups ? "Autres services et formations" : "Services et Formations";
      frame.appendChild(buildGroupHeader(label));
      otherRows.forEach((row) => frame.appendChild(row));
    }
  };

  const startUrgencyTicker = ({ bars, bookingRows, deliveryRows, timeZone, cartRoot }) => {
    const bookingExpiries = bookingRows
      .map((row) => Date.parse(String(row.dataset.bookingExpiresAt || "").trim()))
      .filter((ts) => Number.isFinite(ts));

    const deliveryRow = deliveryRows[0] || null;
    const cutDays = deliveryRow ? parseCutDays(deliveryRow.dataset.cut || "sat,sun") : [];
    const cutoffTime = deliveryRow ? String(deliveryRow.dataset.time || "18:00") : "18:00";

    const update = () => {
      const now = Date.now();
      let hasAny = false;

      if (deliveryRow) {
        const cutoffMs = nextCutoffMs(now, cutoffTime, cutDays, timeZone);
        const remaining = Number.isFinite(cutoffMs) ? cutoffMs - now : Number.NaN;
        if (Number.isFinite(remaining) && remaining > 0 && remaining < 2 * 60 * 60 * 1000) {
          hasAny = true;
          bars.shipping.hidden = false;
          bars.shipping.textContent = `Validation dans ${countdownHHMM(remaining)} pour conserver la pr\u00e9paration du jour.`;
        } else {
          bars.shipping.hidden = true;
          bars.shipping.textContent = "";
        }
      } else {
        bars.shipping.hidden = true;
        bars.shipping.textContent = "";
      }

      const pinnedUntil = parseInt(String(bars.booking.dataset.pinnedUntil || "0"), 10);
      if (pinnedUntil && Number.isFinite(pinnedUntil) && now < pinnedUntil) {
        hasAny = true;
        bars.booking.hidden = false;
      } else {
        if (pinnedUntil) {
          delete bars.booking.dataset.pinnedUntil;
        }

      if (bookingExpiries.length) {
        const nextExpiry = Math.min(...bookingExpiries);
        const remaining = nextExpiry - now;
        if (remaining > 0 && remaining < 30 * 60 * 1000) {
          hasAny = true;
          bars.booking.hidden = false;
          const bookingCountdownTpl = cartRoot ? String(cartRoot.dataset.bookingCountdownText || "").trim() : "";
          if (bookingCountdownTpl && bookingCountdownTpl.includes("[time]")) {
            bars.booking.textContent = bookingCountdownTpl.replace(/\[time\]/g, countdownMMSS(remaining));
          } else if (bookingCountdownTpl) {
            bars.booking.textContent = bookingCountdownTpl;
          } else {
            bars.booking.textContent = `Il vous reste ${countdownMMSS(remaining)} pour finaliser votre paiement.`;
          }
        } else {
          bars.booking.hidden = true;
          bars.booking.textContent = "";
        }
      } else {
        bars.booking.hidden = true;
        bars.booking.textContent = "";
      }

      }

      bars.root.hidden = !hasAny;
    };

    update();
    window.setInterval(update, 1000);
  };

  
  const getCartJson = async () => {
    const res = await fetch("/cart.js", { credentials: "same-origin" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  };

  let calendarApiBasePromise = null;
  const resolveCalendarApiBase = async () => {
    if (calendarApiBasePromise) return calendarApiBasePromise;
    calendarApiBasePromise = (async () => {
      const el = document.getElementById("lfdl-calendar-config");
      const direct = el ? String(el.dataset.calendarBaseUrl || "").trim() : "";
      if (direct && /^https?:\/\//i.test(direct)) return direct;

      const cfgUrl = el ? String(el.dataset.calendarConfigUrl || "").trim() : "";
      if (cfgUrl) {
        const res = await fetch(cfgUrl, { credentials: "same-origin" });
        if (res.ok) {
          const cfg = await res.json().catch(() => ({}));
          const fromFile = String(cfg.baseUrl || "").trim();
          if (fromFile && /^https?:\/\//i.test(fromFile)) return fromFile;
        }
      }

      throw new Error("URL calendrier manquante.");
    })();
    return calendarApiBasePromise;
  };

  const verifyHold = async (holdId) => {
    const apiBase = await resolveCalendarApiBase();
    const res = await fetch(apiBase, {
      method: "POST",
      credentials: "omit",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "verify_hold", hold_id: String(holdId || "").trim() })
    });
    const data = await res.json().catch(() => null);
    if (!res.ok || !data || data.ok !== true || data.valid !== true) {
      const msg = (data && (data.error || data.message)) ? String(data.error || data.message) : "Cr\u00e9neau indisponible.";
      throw new Error(msg);
    }
    return data;
  };

  const pinBookingMessage = (bars, msg, ms = 15000) => {
    if (!bars || !bars.booking || !bars.root) return;
    bars.root.hidden = false;
    bars.booking.hidden = false;
    bars.booking.textContent = String(msg || "");
    bars.booking.dataset.pinnedUntil = String(Date.now() + Math.max(2000, ms));
  };

  const attachCheckoutGuard = ({ bars, cartRoot }) => {
    const form = document.getElementById("lfdl-cart-checkout-form");
    if (!form || form.dataset.lfdlBookingGuard === "1") return;
    form.dataset.lfdlBookingGuard = "1";

    const btn = form.querySelector("#lfdl-checkout-button") || form.querySelector('button[name="checkout"]');

    form.addEventListener("submit", async (e) => {
      if (form.dataset.lfdlBookingChecked === "1") return;
      e.preventDefault();

      let originalText = "";
      if (btn) {
        originalText = btn.textContent;
        btn.disabled = true;
        btn.setAttribute("aria-busy", "true");
        btn.textContent = (cartRoot && String(cartRoot.dataset.bookingVerifyingText || "").trim()) || "V\u00e9rification...";
      }

      try {
        const cart = await getCartJson();
        const holdId = cart && cart.attributes ? String(cart.attributes.booking_hold_id || "").trim() : "";
        const expiresAt = cart && cart.attributes ? String(cart.attributes.booking_hold_expires_at || "").trim() : "";

        if (holdId) {
          if (expiresAt) {
            const ts = Date.parse(expiresAt);
            if (Number.isFinite(ts) && Date.now() > ts) {
              throw new Error((cartRoot && String(cartRoot.dataset.bookingExpiredText || "").trim()) || "Votre cr\u00e9neau a expir\u00e9, veuillez en choisir un autre.");
            }
          }
          await verifyHold(holdId);
        }

        form.dataset.lfdlBookingChecked = "1";
        form.submit();
      } catch (err) {
        pinBookingMessage(bars, err && err.message ? err.message : "Cr\u00e9neau indisponible.");
        if (btn) {
          btn.disabled = false;
          btn.removeAttribute("aria-busy");
          btn.textContent = originalText || btn.textContent;
        }
      }
    });
  };

const init = () => {
    const cart = document.querySelector(".lfdl-cart");
    if (!cart) return;

    const frame = cart.querySelector(".lfdl-cart__frame");
    if (!frame || frame.dataset.lfdlDeliveryGrouped === "true") return;

    const head = frame.querySelector(".lfdl-cart__head");
    const rows = Array.from(frame.querySelectorAll(".lfdl-cart__row"));
    if (!rows.length) return;

    const locale = document.documentElement.lang || "fr-FR";
    const timeZone = String(cart.dataset.shopTimezone || "Europe/Paris").trim() || "Europe/Paris";

    const { lfdl5Rows, lfdl10Rows, astrologyRows, constellationRows, bookingRows, otherRows } = splitRows(rows);
    decorateBookingRows(bookingRows, locale, timeZone);
    renderGroups(frame, lfdl5Rows, lfdl10Rows, astrologyRows, constellationRows, bookingRows, otherRows, locale, timeZone);

    const deliveryRows = [...lfdl5Rows, ...lfdl10Rows].filter((row) => String(row.dataset.lfdlDelivery || "").trim() !== "");
    const bars = ensureUrgencyRoot(frame, head);
    startUrgencyTicker({ bars, bookingRows, deliveryRows, timeZone, cartRoot: cart });
    attachCheckoutGuard({ bars, cartRoot: cart });

    frame.dataset.lfdlDeliveryGrouped = "true";
  };

  onReady(init);
  document.addEventListener("shopify:section:load", init);
})();

